import React from 'react';
import { Button } from '@crisellevaldez/speculo-ui';
import { Hand, ArrowRight } from 'lucide-react';
import { TipBox } from '../components/TipBox';

interface Stage0WelcomeProps {
  userName?: string;
  onStart: () => void;
}

export const Stage0Welcome: React.FC<Stage0WelcomeProps> = ({ userName, onStart }) => {
  return (
    <div className="min-h-[calc(100vh-80px)] flex items-center justify-center px-6 xl:px-10 py-12">
      <div className="max-w-[1500px] w-full grid md:grid-cols-[1.2fr,1fr] gap-5 items-center">
        {/* Left Side - Dashboard Image */}
        <div className="hidden md:block">
          <div className="rounded-lg overflow-hidden">
            <img
              src="https://cdn.prod.website-files.com/685184839bc03f40149a879f/685184839bc03f40149a881c_Speculo_product_dashboard.avif"
              alt="Speculo Dashboard"
              className="w-full h-auto object-cover"
            />
          </div>
        </div>

        {/* Right Side - Welcome Content */}
        <div className="text-center md:text-left">
          {/* Greeting with Hand Icon */}
          <div className="mb-2 xl:mb-4 flex items-center justify-center md:justify-start gap-3">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 dark:from-white dark:via-gray-200 dark:to-white bg-clip-text text-transparent">
              Hi, {userName || 'there'}!
            </h1>
            <Hand className="w-8 h-8 sm:w-10 sm:h-10 text-yellow-500 animate-wave flex-shrink-0" />
          </div>

          {/* Welcome Message */}
          <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mb-3 xl:mb-10">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 dark:from-white dark:via-gray-200 dark:to-white">
              Welcome to
            </h2>
            <img
              src="/speculo.ai-black-with-text.png"
              alt="Speculo.ai"
              className="h-7 sm:h-8 md:h-9 xl:h-11 w-auto"
            />
          </div>

          {/* Description */}
          <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-400 leading-relaxed font-light">
            We're excited to have you here! Let's get your account set up so you can start
            leveraging the power of AI to transform your real estate experience.
          </p>

          {/* Desktop Recommendation */}
          <div className="mb-10">
            <TipBox>
              For the best setup experience, we recommend using a desktop or laptop computer.
            </TipBox>
          </div>

          {/* Start Button */}
          <div className="space-y-4">
            <Button
              onClick={onStart}
              size="lg"
              className="group bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold text-base sm:text-lg px-8 sm:px-12 md:px-14 py-4 sm:py-5 rounded-xl shadow-2xl hover:shadow-green-500/50 transition-all transform hover:scale-105 hover:-translate-y-1 inline-flex items-center gap-2 sm:gap-3"
            >
              Start Setup
              <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 group-hover:animate-arrow-bounce" />
            </Button>

            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-4 flex items-center justify-center md:justify-start gap-2">
              <span className="inline-block w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
              This will only take a few minutes
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
